import React, { useState } from 'react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { AlertCircle, Clock, IndianRupee, Pencil, Wallet } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import {
  DataField,
  DataGrid,
  EmptyRow,
  EmptyState,
  PageBody,
  SectionCard,
  SegmentedControl,
  Table,
  TableCard,
  TBody,
  Td,
  Th,
  THead,
  Tile,
  TileRow
} from '../../../shared/components/ui/primitives';
import {
  getSiteWallet,
  getSiteWalletExpensesPage,
  getSiteWalletLedgerPage,
  reverseSiteWalletExpense
} from '../../services/api/implementation/impl';
import { useSiteWalletRole } from '../../hooks/useSiteWalletAccess';
import { BillRuleModal, WalletActionButtons, useRefreshWallets } from './WalletActions';
import {
  BillLinks,
  HolderName,
  LEDGER_TYPE_LABEL,
  ListFooter,
  NoteModal,
  PAGE_SIZE,
  PAYMENT_MODES,
  ReimbursementChip,
  SmallButton,
  StatusChip,
  WalletStatusPill,
  errorMessage,
  money,
  shortDate
} from './siteWalletUi';

/**
 * One wallet: its numbers, ledger, expenses, top-ups and where the money went
 * by project. Accounts reads it and adds money; the builder side also
 * freezes, reverses an approved expense and settles.
 */
const WalletDetail = () => {
  const { id } = useParams();
  const { onBack } = useSmartBack({ fallback: '/builder/accounts/site-wallets' });
  const role = useSiteWalletRole();
  const refresh = useRefreshWallets();
  const [view, setView] = useState('ledger');
  const [open, setOpen] = useState(null);
  const [reversing, setReversing] = useState(null);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [expensePage, setExpensePage] = useState(1);

  const wallet = useQuery({
    queryKey: ['siteWallet', id],
    queryFn: () => getSiteWallet(id),
    enabled: role.isStaff
  });
  // The ledger and expenses come a page at a time; the wallet's own embedded
  // ledger stops at 500 rows.
  const ledger = useQuery({
    queryKey: ['siteWallet', id, 'ledger', ledgerPage],
    queryFn: () => getSiteWalletLedgerPage(id, { page: ledgerPage, limit: PAGE_SIZE }),
    enabled: role.isStaff && view === 'ledger',
    placeholderData: keepPreviousData
  });
  const expenses = useQuery({
    queryKey: ['siteWallet', id, 'expenses', expensePage],
    queryFn: () =>
      getSiteWalletExpensesPage({ wallet_id: id, page: expensePage, limit: PAGE_SIZE }),
    enabled: role.isStaff && view === 'expenses',
    placeholderData: keepPreviousData
  });

  const reverse = useMutation({
    mutationFn: ({ expenseId, reason }) => reverseSiteWalletExpense(expenseId, { reason }),
    onSuccess: () => {
      refresh();
      setReversing(null);
      toast.success('Expense reversed');
    },
    onError: (err) => toast.error(errorMessage(err, 'Could not reverse the expense'))
  });

  const w = wallet.data;
  const expenseRows = expenses.data?.rows ?? [];
  const ledgerRows = ledger.data?.rows ?? [];
  const closed = w?.status === 'CLOSED';
  const manage = role.builderSide && w && !closed;
  const actions = role.isStaff && w && !closed;

  // Ledger rows carry only a project id; the names come from the same wallet.
  const projectName = new Map();
  (w?.spend_by_project ?? []).forEach((p) => projectName.set(p.project_id, p.project_name));
  expenseRows.forEach((e) => projectName.set(e.project_id, e.project_name));

  return (
    <Layout>
      <Header
        backArrow
        onBack={onBack}
        data={{ name: w?.user?.name ? `${w.user.name}'s wallet` : 'Wallet' }}
      />
      <PageBody>
        {!role.isStaff ? (
          <EmptyState title="Site Wallets is for Accounts and the builder" />
        ) : wallet.isLoading ? (
          <Loader />
        ) : wallet.isError || !w ? (
          <EmptyState title={errorMessage(wallet.error, 'Wallet not found')} />
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <HolderName user={w.user} />
                <WalletStatusPill status={w.status} />
                <span className="text-sm text-gray-500">
                  {w.project_count} project{w.project_count === 1 ? '' : 's'} · opened{' '}
                  {shortDate(w.created_at)}
                  {closed && <> · closed {shortDate(w.closed_at)}</>}
                </span>
              </div>
              {actions && (
                <div className="flex flex-wrap gap-2">
                  <WalletActionButtons
                    size="md"
                    wallet={w}
                    role={role}
                    onSettled={() => wallet.refetch()}
                  />
                </div>
              )}
            </div>

            <TileRow>
              <Tile
                variant="card"
                label="Balance"
                hint="Cash held for the company"
                value={money(w.balance)}
                icon={<Wallet size={16} />}
                tone={Number(w.balance) < 0 ? 'danger' : 'neutral'}
              />
              <Tile
                variant="card"
                label="On hold"
                hint="Waiting for approval"
                value={money(w.on_hold)}
                icon={<Clock size={16} />}
              />
              <Tile
                variant="card"
                label="Available"
                hint="Balance less on hold"
                value={money(w.available)}
                icon={<IndianRupee size={16} />}
                tone="success"
              />
              <Tile
                variant="card"
                label="Owed to them"
                hint="Own pocket, approved"
                value={money(w.owed_to_holder)}
                icon={<AlertCircle size={16} />}
                tone={Number(w.owed_to_holder) > 0 ? 'warning' : 'neutral'}
              />
            </TileRow>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <SectionCard
                title="Bill photo"
                action={
                  manage && (
                    <SmallButton onClick={() => setOpen('billRule')}>
                      <Pencil size={12} /> Edit
                    </SmallButton>
                  )
                }
              >
                <DataGrid columns={3}>
                  <DataField label="Bill needed above" value={money(w.bill_required_above)} />
                </DataGrid>
                {closed && (
                  <p className="mt-3 text-sm text-gray-500">
                    Settled: {money(w.settle_returned)} handed back
                    {Number(w.settle_payout) > 0 &&
                      `, ${money(w.settle_payout)} owed to them (${w.settle_payout_paid_at ? 'paid' : 'due'})`}
                    .
                  </p>
                )}
              </SectionCard>

              <SectionCard title="Spend by project" subtitle="Approved expenses">
                {!w.spend_by_project?.length ? (
                  <p className="text-sm text-gray-500">No approved spend yet.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-sm text-gray-500">
                      <tr>
                        <th className="py-1 text-left font-medium">Project</th>
                        <th className="py-1 text-right font-medium">Wallet</th>
                        <th className="py-1 text-right font-medium">Own pocket</th>
                        <th className="py-1 text-right font-medium">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {w.spend_by_project.map((p) => (
                        <tr key={p.project_id} className="border-t border-gray-100">
                          <td className="py-1.5 text-gray-800">{p.project_name ?? '—'}</td>
                          <td className="py-1.5 text-right tabular-nums text-gray-600">
                            {money(p.wallet_amount)}
                          </td>
                          <td className="py-1.5 text-right tabular-nums text-gray-600">
                            {money(p.own_amount)}
                          </td>
                          <td className="py-1.5 text-right font-medium tabular-nums">
                            {money(p.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </SectionCard>
            </div>

            <SegmentedControl
              ariaLabel="Wallet history"
              size="sm"
              value={view}
              onChange={setView}
              options={[
                { value: 'ledger', label: 'Ledger' },
                { value: 'expenses', label: 'Expenses' },
                { value: 'topups', label: 'Money added' }
              ]}
            />

            {view === 'ledger' &&
              (ledger.isLoading ? (
                <Loader />
              ) : (
                <>
                  <TableCard>
                    <Table>
                      <THead>
                        <tr>
                          <Th>When</Th>
                          <Th>Type</Th>
                          <Th>Project</Th>
                          <Th>Reason</Th>
                          <Th align="right">Amount</Th>
                        </tr>
                      </THead>
                      <TBody>
                        {!ledgerRows.length && (
                          <EmptyRow colSpan={5}>No ledger entries yet.</EmptyRow>
                        )}
                        {ledgerRows.map((l) => (
                          <tr key={l.id}>
                            <Td className="whitespace-nowrap text-gray-600">
                              {shortDate(l.created_at)}
                            </Td>
                            <Td>{LEDGER_TYPE_LABEL[l.type] ?? l.type}</Td>
                            <Td className="text-gray-600">
                              {l.project_id
                                ? (projectName.get(l.project_id) ?? `#${l.project_id}`)
                                : '—'}
                            </Td>
                            <Td className="max-w-xs text-sm text-gray-600">{l.reason || '—'}</Td>
                            <Td
                              align="right"
                              numeric
                              className={`font-medium ${Number(l.amount) < 0 ? 'text-danger-fg' : 'text-success-fg'}`}
                            >
                              {Number(l.amount) > 0 ? '+' : ''}
                              {money(l.amount)}
                            </Td>
                          </tr>
                        ))}
                      </TBody>
                    </Table>
                  </TableCard>
                  <ListFooter
                    meta={ledger.data?.meta}
                    page={ledgerPage}
                    onPageChange={setLedgerPage}
                    isFetching={ledger.isFetching}
                  />
                </>
              ))}

            {view === 'expenses' &&
              (expenses.isLoading ? (
                <Loader />
              ) : (
                <>
                  <TableCard>
                    <Table>
                      <THead>
                        <tr>
                          <Th>Spent on</Th>
                          <Th>Project · Category</Th>
                          <Th align="right">Amount</Th>
                          <Th>Bill</Th>
                          <Th>Status</Th>
                          <Th>Note</Th>
                          {manage && <Th />}
                        </tr>
                      </THead>
                      <TBody>
                        {!expenseRows.length && (
                          <EmptyRow colSpan={manage ? 7 : 6}>No expenses yet.</EmptyRow>
                        )}
                        {expenseRows.map((e) => (
                          <tr key={e.id}>
                            <Td className="whitespace-nowrap">{shortDate(e.spent_on)}</Td>
                            <Td>
                              <div className="text-gray-900">{e.project_name ?? '—'}</div>
                              <div className="text-sm text-gray-500">{e.category_name ?? '—'}</div>
                            </Td>
                            <Td align="right" numeric>
                              <div className="font-semibold">{money(e.amount)}</div>
                              {Number(e.own_amount) > 0 && (
                                <div className="text-sm text-gray-500">
                                  {money(e.own_amount)} own pocket
                                </div>
                              )}
                            </Td>
                            <Td>
                              <BillLinks urls={e.bill_urls} />
                            </Td>
                            <Td>
                              <StatusChip status={e.status} />
                              {e.status === 'SUBMITTED' && (
                                <div className="mt-1 text-sm text-gray-500">
                                  On{' '}
                                  {e.approver_role === 'ACCOUNTS'
                                    ? (e.approver_name ?? 'Accountant')
                                    : 'Builder'}
                                </div>
                              )}
                              {e.reject_reason && (
                                <div className="mt-1 text-sm text-danger-fg">{e.reject_reason}</div>
                              )}
                              {e.reverse_reason && (
                                <div className="mt-1 text-sm text-danger-fg">
                                  {e.reverse_reason}
                                </div>
                              )}
                              <ReimbursementChip expense={e} />
                            </Td>
                            <Td className="max-w-xs text-sm text-gray-700">{e.note || '—'}</Td>
                            {manage && (
                              <Td>
                                {e.status === 'APPROVED' && (
                                  <SmallButton tone="danger" onClick={() => setReversing(e)}>
                                    Reverse
                                  </SmallButton>
                                )}
                              </Td>
                            )}
                          </tr>
                        ))}
                      </TBody>
                    </Table>
                  </TableCard>
                  <ListFooter
                    meta={expenses.data?.meta}
                    page={expensePage}
                    onPageChange={setExpensePage}
                    isFetching={expenses.isFetching}
                  />
                </>
              ))}

            {view === 'topups' && (
              <TableCard>
                <Table>
                  <THead>
                    <tr>
                      <Th>Date</Th>
                      <Th align="right">Amount</Th>
                      <Th>Mode</Th>
                      <Th>Reference / transaction ID</Th>
                      <Th>Note</Th>
                    </tr>
                  </THead>
                  <TBody>
                    {!w.top_ups?.length && <EmptyRow colSpan={5}>No top-ups yet.</EmptyRow>}
                    {(w.top_ups ?? []).map((t) => (
                      <tr key={t.id}>
                        <Td className="whitespace-nowrap text-gray-600">
                          {shortDate(t.paid_at ?? t.created_at)}
                        </Td>
                        <Td align="right" numeric className="font-semibold">
                          {money(t.amount)}
                        </Td>
                        <Td className="text-gray-700">
                          {PAYMENT_MODES.find((m) => m.value === t.mode)?.label ?? t.mode ?? '—'}
                        </Td>
                        <Td className="text-sm text-gray-600">{t.reference || '—'}</Td>
                        <Td className="max-w-xs text-sm text-gray-700">{t.note || '—'}</Td>
                      </tr>
                    ))}
                  </TBody>
                </Table>
              </TableCard>
            )}
          </>
        )}
      </PageBody>

      {open === 'billRule' && w && <BillRuleModal wallet={w} onClose={() => setOpen(null)} />}
      {reversing && (
        <NoteModal
          title={`Reverse ${money(reversing.amount)} (${reversing.category_name ?? 'expense'})`}
          intro="The wallet part comes back to the wallet. An own-pocket part still due is cancelled; one already paid back is recovered from the wallet."
          label="Reason"
          placeholder="Why it is being undone"
          confirm="Reverse expense"
          danger
          minLength={3}
          busy={reverse.isPending}
          onClose={() => setReversing(null)}
          onSubmit={(reason) => reverse.mutate({ expenseId: reversing.id, reason })}
        />
      )}
    </Layout>
  );
};

export default WalletDetail;
